import React, { useState, useMemo } from "react";
import CommonInfo from "./CommonInfo";
import ProductRow from "./ProductRow";
import { useDebounce } from "../hooks/useDebounce";

const HoSoCard = ({ data, baoGiaList = [], thongBaoPhiList = [] }) => {
  // All hooks must be called before any early returns
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filter products based on debounced search query (by ky_hieu)
  const filteredProducts = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    if (!debouncedSearchQuery.trim()) {
      return data;
    }

    const searchLower = debouncedSearchQuery.toLowerCase();
    return data.filter((product) =>
      product.kyHieu?.toLowerCase().includes(searchLower),
    );
  }, [data, debouncedSearchQuery]);

  // Calculate completed products count
  const completedCount = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.filter(
      (product) =>
        product.trangThai === "Hoàn thành" ||
        product.trangThai === "Đã trả kết quả",
    ).length;
  }, [data]);

  // Early return after all hooks
  if (!data || data.length === 0) {
    return null;
  }

  const isMultipleProducts = data.length > 1;
  const commonData = data[0];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const displaySearchTerm =
    searchQuery.length > 50 ? searchQuery.slice(0, 50) + "..." : searchQuery;

  return (
    <div className="p-6 sm:p-8">
      {/* Header Section */}
      <div className="text-center pb-6 border-b-2 border-neutral-borderAccent">
        {/* <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <svg
            className="w-8 h-8 text-primary-main"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div> */}
        <h2 className="text-2xl font-bold text-neutral-textPrimary mb-1 font-heading">
          HỒ SƠ CHỨNG NHẬN HỢP QUY
        </h2>
        <p className="text-lg text-primary-main font-semibold font-body">
          Số: {commonData.soHoSo}
        </p>
      </div>

      {/* Common Information Section */}
      <div className="mt-6">
        <CommonInfo
          data={commonData}
          allProducts={data}
          baoGiaList={baoGiaList}
          thongBaoPhiList={thongBaoPhiList}
        />
      </div>

      {/* Products Section */}
      <div className="mt-6">
        {isMultipleProducts && (
          <div className="mb-4">
            {/* Responsive Toolbar: Title + Search */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              {/* Left: Section Title */}
              <h3 className="text-xl font-bold text-neutral-textPrimary flex items-center font-heading">
                <svg
                  className="w-6 h-6 text-primary-main mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <span>Danh sách sản phẩm</span>
              </h3>

              {/* Right: Search Input */}
              <div className="relative w-full md:w-80">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-neutral-textSecondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Tìm kiếm ký hiệu sản phẩm..."
                  maxLength={255}
                  className="block w-full pl-10 pr-10 py-2.5 border border-neutral-borderAccent rounded-md-tqc focus:ring-2 focus:ring-primary-main focus:border-primary-main transition-normal text-neutral-textPrimary placeholder-neutral-textSecondary font-body text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-textSecondary hover:text-neutral-textPrimary transition-normal"
                    aria-label="Xóa tìm kiếm"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Product List */}
        <div className="border border-neutral-borderAccent rounded-lg-tqc shadow-md-tqc overflow-hidden bg-neutral-bgPaper">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product, index) => (
              <ProductRow
                key={product.id}
                product={product}
                index={index}
                isMultiple={isMultipleProducts}
              />
            ))
          ) : (
            <div className="p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-neutral-textSecondary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="mt-4 text-neutral-textSecondary font-body">
                Không tìm thấy sản phẩm nào phù hợp với "
                <span className="font-semibold text-blue-700 break-all">
                  {displaySearchTerm}
                </span>
                "
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-6 p-4 bg-primary-50 rounded-md-tqc border border-primary-200 shadow-sm-tqc">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-status-info"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-primary-dark font-body">
              <strong>Lưu ý:</strong> Nếu có thắc mắc về hồ sơ, vui lòng liên hệ
              bộ phận tiếp nhận hồ sơ theo số điện thoại hotline.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoSoCard;
